import React from 'react';
import { Anchor, Button, CopyButton, Group, Stack, Text } from '@mantine/core';
import { IconCheck, IconCopy, IconExternalLink } from '@tabler/icons-react';
import FlowAlert from '@/components/auth/flow/components/parts/Flow.Alert';
import FlowCodeField from '@/components/auth/flow/components/parts/Flow.Code.Field';
import FlowWorking from '@/components/auth/flow/components/screens/Flow.Working';
import { AUTH_CODE_LENGTH } from '@/function/auth/code.sanitize';
import totpSecretOf from '@/function/auth/totp.secret.of';
import type { TypeFlowScreenProps } from '@/types/auth/types.auth.flow';

type Props = TypeFlowScreenProps & { step: string; uri: string | null; secret: string | null };

const FlowTotpSetup: React.FC<Props> = ({ flow, message, step, uri, secret }) => {
    const [code, setCode] = React.useState('');
    const [local, setLocal] = React.useState<string | null>(null);
    const fieldError = local ?? (message?.field === 'code' ? message.text : null);
    const key = secret ?? totpSecretOf(uri);

    if (!uri || !uri.startsWith('otpauth://')) return <FlowWorking label="Preparo la chiave dell’app…" />;

    const send = (value: string) => {
        if (flow.busy) return;
        if (value.length !== AUTH_CODE_LENGTH) {
            setLocal(`Il codice ha ${AUTH_CODE_LENGTH} cifre`);
            return;
        }
        void flow.submit(step, { code: value });
    };

    return (
        <form onSubmit={(event) => { event.preventDefault(); send(code); }} noValidate>
            <Stack gap="md">
                <FlowAlert message={message} field="code" />
                <Text size="sm" c="dimmed">Aggiungi un account nell’app di autenticazione (Google Authenticator, Microsoft Authenticator, 1Password, Bitwarden…) scrivendo questa chiave, oppure aprila direttamente dal telefono. Poi inserisci il codice a 6 cifre che ti mostra.</Text>
                {key ? (
                    <Group justify="center" gap="xs" wrap="wrap">
                        <Text ff="monospace" fw={600} ta="center" style={{ overflowWrap: 'anywhere' }}>{key}</Text>
                        <CopyButton value={key.replace(/\s/g, '')}>
                            {({ copied, copy }) => (
                                <Button size="xs" variant="light" mih={44} leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />} onClick={copy}>
                                    {copied ? 'COPIATA' : 'COPIA'}
                                </Button>
                            )}
                        </CopyButton>
                    </Group>
                ) : null}
                <Group justify="center">
                    <Anchor href={uri} size="sm" style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <IconExternalLink size={16} aria-hidden />
                        Apri nell’app di autenticazione
                    </Anchor>
                </Group>
                <FlowCodeField label="Codice dell’app" value={code} error={fieldError} disabled={flow.busy} onChange={(value) => { setLocal(null); setCode(value); }} onComplete={send} />
                <Button type="submit" radius="md" fullWidth loading={flow.busy} disabled={flow.busy || code.length !== AUTH_CODE_LENGTH}>ATTIVA</Button>
            </Stack>
        </form>
    );
};

export default FlowTotpSetup;
